import { useAccountReceivableList } from '@/core/domain/financial/AccountReceivable/AccountReceivable.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { Modal, ModalProps } from 'antd';
import React, { useState } from 'react';
import InfiniteTable from '../InfiniteTable';
import {
  AccountReceivable,
  AccountReceivableListRequest,
  AccountReceivableOrderColumn,
  AccountReceivableSituation,
  AccountReceivableSituationColor,
  AccountRecivableSituationType,
} from '@/core/domain/financial/AccountReceivable/AccountReceivable.types';
import { ColumnsType, SorterResult } from 'antd/es/table/interface';
import { COLUMNS_WIDTH, tableSorterOrder } from '@/utils/table';
import { useIntl } from 'react-intl';
import StatusTag from '../StatusTag';
import moment from 'moment';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import EditIcon from '../EditIcon';
import AppPageHeader from '../AppPageHeader';
import { useDebounce } from '@/hooks';
import { quantityFormatter } from '@/utils/number';

interface Props extends ModalProps {
  open: boolean;
  onCancel: VoidFunction;
  queryFilter?: AccountReceivableListRequest;
}

const AccountReceivableListModal: React.FC<Props> = ({
  open,
  onCancel,
  queryFilter,
  ...props
}) => {
  const intl = useIntl();
  const [queryParams, setQueryParams] = useState<AccountReceivableListRequest>({
    order: 'DESC',
    orderColumns: AccountReceivableOrderColumn.code,
    ...queryFilter,
  });

  const queryDebounce = useDebounce({ ...queryParams, ...queryFilter }, 500);
  const { data, isLoading, fetchNextPage, hasNextPage } =
    useAccountReceivableList(queryDebounce, open);

  const accountReceivableItems = flatInfiniteData(data);

  const columns: ColumnsType<AccountReceivable> = [
    {
      width: COLUMNS_WIDTH.price,
      dataIndex: 'code',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.code',
      }),
      sorter: true,
      key: AccountReceivableOrderColumn.code,
    },
    {
      width: 220,
      dataIndex: 'companyName',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.companies',
      }),
      sorter: true,
      key: AccountReceivableOrderColumn.company,
      ellipsis: true,
    },
    {
      width: 220,
      dataIndex: 'debtor',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.debtor',
      }),
      sorter: true,
      key: AccountReceivableOrderColumn.debtor,
      ellipsis: true,
    },
    {
      width: 100,
      dataIndex: 'dueDate',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.dueDate',
      }),
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      sorter: true,
      key: AccountReceivableOrderColumn.dueDate,
    },
    {
      width: COLUMNS_WIDTH.price,
      dataIndex: 'situation',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.situation',
      }),
      render: (situation: AccountReceivableSituation) => (
        <StatusTag
          color={AccountReceivableSituationColor[situation]}
          label={AccountRecivableSituationType[situation]}
        />
      ),
    },
    {
      width: COLUMNS_WIDTH.price,
      dataIndex: 'installmentValue',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.value',
      }),
      sorter: true,
      align: 'right',
      key: AccountReceivableOrderColumn.value,
      render: (_, item) =>
        `R$ ${quantityFormatter(
          item.installmentValue + (item.discountIncrease ?? 0),
        )}`,
    },
    {
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.actions',
      }),
      width: 50,
      align: 'right',
      fixed: 'right',
      render: (_, item) => (
        <EditIcon
          iconProps={{
            onClick: () =>
              window.open(
                `/app/financial/billToReceiver/releaseBillToReceiver/${item.accountReceivableId}/installment/${item.id}`,
                '_blank',
              ),
          }}
          disabled={
            !hasPermissions([Authorities.FINANCIAL_ACCOUNT_RECEIVABLE_EDIT])
          }
        />
      ),
    },
  ];

  function handleOrderAccountReceivale(
    sorter: SorterResult<AccountReceivable>,
  ) {
    if (sorter && sorter?.columnKey) {
      setQueryParams((prev) => {
        if (sorter && sorter?.columnKey) {
          const order = sorter.order
            ? tableSorterOrder[sorter.order]
            : undefined;

          if (
            prev.orderColumns ===
            (sorter.columnKey as AccountReceivableOrderColumn)
          ) {
            return {
              ...prev,
              order,
            };
          }

          return {
            ...prev,
            order: tableSorterOrder.ascend,
            orderColumns: sorter.columnKey as AccountReceivableOrderColumn,
          };
        }

        return prev;
      });
    }
  }

  const handleCancel = () => {
    setQueryParams({
      order: 'DESC',
      orderColumns: AccountReceivableOrderColumn.code,
    });

    onCancel();
  };

  return (
    <Modal
      {...props}
      title="Lançamentos do Contas a Receber"
      width={1000}
      open={open}
      onCancel={handleCancel}
      footer={false}
    >
      <AppPageHeader
        onSearch={(value) =>
          setQueryParams((prev) => ({ ...prev, search: value }))
        }
        searchValue={queryParams.search}
      />

      <InfiniteTable<AccountReceivable>
        columns={columns}
        dataSource={accountReceivableItems}
        loading={isLoading}
        rowKey={(row) => row.id}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        onChange={(_p, _f, sorter) => {
          if (!Array.isArray(sorter)) {
            handleOrderAccountReceivale(sorter);
          }
        }}
      />
    </Modal>
  );
};

export default AccountReceivableListModal;
